import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { useGuidedTour } from '@/context/GuidedTourContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Users, MessageSquare, FileText, CheckSquare, Network,
  Mic, Share2, ChevronRight, Loader2, Sparkles, Zap, BookOpen, Mail,
  Video, Shield, TrendingDown, Clock, AlertTriangle, Plus,
  BarChart3, Activity, Scissors, Play, Calendar, ArrowRight, Heart, Brain, Target,
  Compass,
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import ShareViaEmail from '@/components/ShareViaEmail';
import RecentlyShippedCard from '@/components/dashboard/RecentlyShippedCard';
import KMPulseWidget from '@/components/dashboard/KMPulseWidget';
import TeamPresenceWidget from '@/components/dashboard/TeamPresenceWidget';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getTimeAgo(dateStr) {
  if (!dateStr) return '';
  const now = new Date();
  const d = new Date(dateStr);
  const diff = Math.floor((now - d) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

const DashboardPage = () => {
  const { user, token } = useAuth();
  const { startTour } = useGuidedTour();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [feed, setFeed] = useState([]);
  const [feedLoading, setFeedLoading] = useState(true);
  const [digest, setDigest] = useState(null);
  const [generatingDigest, setGeneratingDigest] = useState(false);
  const [digestShareOpen, setDigestShareOpen] = useState(false);

  useEffect(() => {
    fetchStats();
    fetchFeed();
    fetchLatestDigest();
  }, []);

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/dashboard/stats`, { headers: { Authorization: `Bearer ${token}` } });
      setStats(res.data);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };

  const fetchFeed = async () => {
    setFeedLoading(true);
    try {
      const res = await axios.get(`${API}/feed`, { params: { skip: 0, limit: 6 }, headers: { Authorization: `Bearer ${token}` } });
      setFeed(res.data.items || []);
    } catch { /* silent */ }
    finally { setFeedLoading(false); }
  };

  const fetchLatestDigest = async () => {
    try {
      const res = await axios.get(`${API}/digest/latest`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data?.digest_id) setDigest(res.data);
    } catch { /* silent */ }
  };

  const handleGenerateDigest = async () => {
    setGeneratingDigest(true);
    try {
      const res = await axios.post(`${API}/digest/generate`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setDigest(res.data);
      toast.success('Weekly digest generated!');
    } catch { toast.error('Failed to generate digest'); }
    finally { setGeneratingDigest(false); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="dashboard-loading">
        <Loader2 className="w-8 h-8 animate-spin text-[#002FA7]" />
      </div>
    );
  }

  const healthScore = stats?.health_score ?? 100;
  const healthColor = healthScore >= 75 ? '#34C759' : healthScore >= 50 ? '#FF9500' : '#FF3B30';

  return (
    <div data-testid="dashboard-page" className="space-y-6">
      {/* Header with Quick Actions */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold text-[#0A0A0A]">
            Welcome back, {user?.name?.split(' ')[0]}
          </h1>
          <div className="flex items-center gap-3 mt-1 flex-wrap">
            <p className="text-[#4B5563] text-sm">Your knowledge command center</p>
            <button
              type="button"
              onClick={() => startTour('getting-started')}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium text-[#5856D6] bg-[#5856D6]/8 hover:bg-[#5856D6]/16 border border-[#5856D6]/20 transition-colors"
              data-testid="retake-tour-btn"
              title="Re-launch the Getting Started tour"
            >
              <Compass className="w-3 h-3" />
              Take the tour
            </button>
          </div>
        </div>
        <div className="flex gap-2" data-testid="quick-actions">
          <Button size="sm" className="bg-[#002FA7] hover:bg-[#00227A]" onClick={() => navigate('/articles')} data-testid="quick-new-article">
            <Plus className="w-3.5 h-3.5 mr-1" />Article
          </Button>
          <Button size="sm" variant="outline" onClick={() => navigate('/tasks')} data-testid="quick-new-task">
            <Plus className="w-3.5 h-3.5 mr-1" />Task
          </Button>
          <Button size="sm" variant="outline" onClick={() => navigate('/webinars')} data-testid="quick-new-webinar">
            <Video className="w-3.5 h-3.5 mr-1" />Webinar
          </Button>
        </div>
      </div>

      {/* Top Row: Health Score + Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-testid="dashboard-metrics">
        {/* Knowledge Health Score */}
        <Card className="border-[#E5E7EB] md:row-span-2" data-testid="health-score-card">
          <CardContent className="p-5 flex flex-col items-center justify-center h-full">
            <div className="relative w-28 h-28 mb-3">
              <svg className="w-28 h-28 -rotate-90" viewBox="0 0 120 120">
                <circle cx="60" cy="60" r="52" fill="none" stroke="#E5E7EB" strokeWidth="10" />
                <circle cx="60" cy="60" r="52" fill="none" stroke={healthColor} strokeWidth="10"
                  strokeLinecap="round" strokeDasharray={`${(healthScore / 100) * 327} 327`} />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-3xl font-bold" style={{ color: healthColor }}>{healthScore}</span>
                <span className="text-[10px] text-[#9CA3AF] uppercase tracking-wider">Health</span>
              </div>
            </div>
            <h3 className="text-sm font-semibold text-[#0A0A0A]">Knowledge Health</h3>
            <p className="text-xs text-[#9CA3AF] text-center mt-1">
              {healthScore >= 75 ? 'Your knowledge base is healthy' : healthScore >= 50 ? 'Some assets need attention' : 'Critical assets need review'}
            </p>
            {stats?.degradation_critical > 0 && (
              <Badge className="mt-2 bg-[#FF3B30]/10 text-[#FF3B30] text-[10px]">
                <AlertTriangle className="w-3 h-3 mr-1" />{stats.degradation_critical} critical
              </Badge>
            )}
            <Button size="sm" variant="ghost" className="mt-2 text-xs text-[#002FA7]" onClick={() => navigate('/knowledge-degradation')} data-testid="go-degradation">
              View Details <ChevronRight className="w-3 h-3 ml-1" />
            </Button>
          </CardContent>
        </Card>

        {/* Metric Cards */}
        {[
          { label: 'My Open Tasks', value: stats?.my_tasks_pending || 0, icon: CheckSquare, color: '#007AFF', link: '/tasks', sub: stats?.my_tasks_overdue > 0 ? `${stats.my_tasks_overdue} overdue` : null, subColor: '#FF3B30' },
          { label: 'Unread Messages', value: stats?.messages_unread || 0, icon: MessageSquare, color: '#34C759', link: '/inbox' },
          { label: 'Articles', value: stats?.articles_count || 0, icon: FileText, color: '#AF52DE', link: '/articles', sub: stats?.recent_articles_7d > 0 ? `+${stats.recent_articles_7d} this week` : null, subColor: '#34C759' },
          { label: 'Active Transfers', value: stats?.active_transfers || 0, icon: Share2, color: '#FF9500', link: '/knowledge-transfer' },
          { label: 'Knowledge Maps', value: stats?.knowledge_maps_count || 0, icon: Network, color: '#00C7BE', link: '/knowledge-maps' },
          { label: 'Recordings', value: stats?.total_recordings || 0, icon: Video, color: '#FF2D55', link: '/webinars', sub: stats?.total_clips > 0 ? `${stats.total_clips} clips` : null, subColor: '#FF9500' },
        ].map((m, i) => {
          const Icon = m.icon;
          return (
            <Link key={i} to={m.link}>
              <Card className="border-[#E5E7EB] hover:border-[#002FA7]/30 transition-all hover:shadow-sm h-full" data-testid={`metric-${i}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-[#9CA3AF] font-medium">{m.label}</span>
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: m.color + '15' }}>
                      <Icon className="w-4 h-4" style={{ color: m.color }} />
                    </div>
                  </div>
                  <p className="text-2xl font-bold text-[#0A0A0A]">{m.value}</p>
                  {m.sub && <span className="text-[10px] font-medium" style={{ color: m.subColor }}>{m.sub}</span>}
                </CardContent>
              </Card>
            </Link>
          );
        })}
      </div>

      {/* Middle Row: Upcoming Webinars + Recent Activity + AI Digest */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Upcoming Webinars */}
        <Card className="border-[#E5E7EB]" data-testid="upcoming-webinars-widget">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm flex items-center gap-2"><Video className="w-4 h-4 text-[#FF2D55]" />Upcoming Webinars</CardTitle>
              <Button variant="ghost" size="sm" className="h-7 text-xs text-[#002FA7]" onClick={() => navigate('/webinars')}>View all</Button>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            {!stats?.upcoming_webinars?.length ? (
              <div className="text-center py-6 text-[#9CA3AF]">
                <Calendar className="w-8 h-8 mx-auto mb-2 text-[#E5E7EB]" />
                <p className="text-xs">No upcoming webinars</p>
                <Button size="sm" variant="outline" className="mt-2 text-xs" onClick={() => navigate('/webinars')}>
                  <Plus className="w-3 h-3 mr-1" />Schedule one
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                {stats.upcoming_webinars.map((w, i) => (
                  <Link key={i} to="/webinars">
                    <div className="flex items-center gap-3 p-2 rounded-lg hover:bg-[#F3F4F6] transition-colors" data-testid={`upcoming-webinar-${i}`}>
                      <div className="w-10 h-10 rounded-lg bg-[#FF2D55]/10 flex items-center justify-center flex-shrink-0">
                        <Play className="w-4 h-4 text-[#FF2D55]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-[#0A0A0A] truncate">{w.title}</p>
                        <p className="text-[10px] text-[#9CA3AF]">
                          {w.scheduled_at ? format(new Date(w.scheduled_at), 'MMM d, h:mm a') : ''} · {w.duration_minutes}min
                        </p>
                      </div>
                      <Badge variant="secondary" className="text-[9px]">{w.attendees?.length || 0} joined</Badge>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Activity */}
        <Card className="border-[#E5E7EB]" data-testid="recent-activity-widget">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2"><Activity className="w-4 h-4 text-[#007AFF]" />Recent Activity</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            {feedLoading ? (
              <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-[#002FA7]" /></div>
            ) : feed.length === 0 ? (
              <div className="text-center py-6 text-[#9CA3AF]">
                <Activity className="w-8 h-8 mx-auto mb-2 text-[#E5E7EB]" />
                <p className="text-xs">No recent activity</p>
              </div>
            ) : (
              <div className="space-y-1">
                {feed.slice(0, 6).map((item, i) => {
                  const typeMap = { article: { icon: FileText, color: '#AF52DE' }, task: { icon: CheckSquare, color: '#007AFF' }, voice_journal: { icon: Mic, color: '#34C759' }, knowledge_transfer: { icon: Share2, color: '#FF9500' } };
                  const cfg = typeMap[item.type] || typeMap.article;
                  const Icon = cfg.icon;
                  return (
                    <div key={i} className="flex items-center gap-2.5 p-1.5 rounded hover:bg-[#F3F4F6] transition-colors" data-testid={`activity-${i}`}>
                      <div className="w-7 h-7 rounded flex items-center justify-center flex-shrink-0" style={{ backgroundColor: cfg.color + '15' }}>
                        <Icon className="w-3.5 h-3.5" style={{ color: cfg.color }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-[#0A0A0A] truncate">{item.title}</p>
                        <p className="text-[10px] text-[#9CA3AF]">{getTimeAgo(item.created_at)}</p>
                      </div>
                      {item.relevance >= 0.8 && <div className="w-1.5 h-1.5 rounded-full bg-[#002FA7]" />}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* AI Weekly Digest */}
        <Card className="border-[#E5E7EB]" data-testid="digest-widget">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm flex items-center gap-2"><Zap className="w-4 h-4 text-[#002FA7]" />AI Weekly Digest</CardTitle>
              <div className="flex gap-1">
                <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={handleGenerateDigest} disabled={generatingDigest} data-testid="generate-digest-btn">
                  {generatingDigest ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                </Button>
                {digest && (
                  <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setDigestShareOpen(true)} data-testid="share-digest-btn">
                    <Mail className="w-3 h-3" />
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            {!digest ? (
              <div className="text-center py-6">
                <BookOpen className="w-8 h-8 text-[#E5E7EB] mx-auto mb-2" />
                <p className="text-xs text-[#9CA3AF]">Generate an AI-powered summary of your week</p>
                <Button size="sm" className="mt-2 bg-[#002FA7] hover:bg-[#00227A] text-xs" onClick={handleGenerateDigest} disabled={generatingDigest} data-testid="generate-digest-cta">
                  <Sparkles className="w-3 h-3 mr-1" />Generate Digest
                </Button>
              </div>
            ) : (
              <div data-testid="digest-content">
                <div className="flex gap-3 mb-3">
                  {[
                    { label: 'Articles', value: digest.stats?.articles_count || 0, color: '#AF52DE' },
                    { label: 'Tasks', value: digest.stats?.tasks_count || 0, color: '#007AFF' },
                    { label: 'Notes', value: digest.stats?.voice_count || 0, color: '#34C759' },
                  ].map((s, i) => (
                    <div key={i} className="flex-1 text-center p-2 rounded-lg" style={{ backgroundColor: s.color + '10' }}>
                      <p className="text-lg font-bold" style={{ color: s.color }}>{s.value}</p>
                      <p className="text-[10px] text-[#9CA3AF]">{s.label}</p>
                    </div>
                  ))}
                </div>
                <div className="text-xs text-[#4B5563] leading-relaxed max-h-32 overflow-y-auto" data-testid="digest-text">
                  {digest.content?.split('\n').slice(0, 6).map((line, i) => {
                    if (!line.trim()) return null;
                    if (line.startsWith('**') || line.startsWith('###') || line.startsWith('##'))
                      return <p key={i} className="font-semibold text-[#0A0A0A] mt-1.5">{line.replace(/[#*]/g, '').trim()}</p>;
                    if (line.startsWith('- '))
                      return <p key={i} className="ml-2">· {line.slice(2)}</p>;
                    return <p key={i}>{line}</p>;
                  })}
                </div>
                <p className="text-[10px] text-[#9CA3AF] mt-2">Generated {getTimeAgo(digest.created_at)}</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Team Chat & Presence — elevated live team awareness */}
      <TeamPresenceWidget />

      {/* Recently Shipped — customer-facing changelog highlights */}
      <RecentlyShippedCard />

      {/* KM Pulse — compact Capability Assessment scorecard */}
      <KMPulseWidget />

      {/* Bottom Row: Module Quick Links */}
      <Card className="border-[#E5E7EB]" data-testid="module-links">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2"><BarChart3 className="w-4 h-4 text-[#9CA3AF]" />Knowledge Tools</CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-2">
            {[
              { label: 'Knowledge Engine', icon: Brain, color: '#002FA7', path: '/pkm' },
              { label: 'Voice Journal', icon: Mic, color: '#34C759', path: '/voice-journal' },
              { label: 'Knowledge Maps', icon: Network, color: '#00C7BE', path: '/knowledge-maps' },
              { label: 'Webinars', icon: Video, color: '#FF2D55', path: '/webinars' },
              { label: 'Degradation', icon: TrendingDown, color: '#FF9500', path: '/knowledge-degradation' },
              { label: 'Audit', icon: Shield, color: '#5856D6', path: '/knowledge-audit' },
              { label: 'Gap Predictions', icon: Sparkles, color: '#AF52DE', path: '/knowledge-predictions' },
            ].map((tool, i) => {
              const Icon = tool.icon;
              return (
                <Link key={i} to={tool.path}>
                  <div className="flex items-center gap-2 p-2.5 rounded-lg border border-[#E5E7EB] hover:border-[#002FA7]/30 hover:shadow-sm transition-all" data-testid={`tool-${i}`}>
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: tool.color + '15' }}>
                      <Icon className="w-4 h-4" style={{ color: tool.color }} />
                    </div>
                    <span className="text-xs font-medium text-[#4B5563]">{tool.label}</span>
                  </div>
                </Link>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Share Digest Dialog */}
      {digest && digestShareOpen && (
        <ShareViaEmail
          open={digestShareOpen}
          onClose={() => setDigestShareOpen(false)}
          contentType="digest"
          contentId={digest.digest_id}
          contentTitle={`Weekly Digest — ${new Date(digest.created_at).toLocaleDateString()}`}
        />
      )}
    </div>
  );
};

export default DashboardPage;
